/**
 * P7(a) A1 — useMyTeamInvites hook.
 *
 * The signed-in athlete's own claimed-pending team invites, driving the
 * acceptance banner on Today + Team ("{Coach} invited you to join {Team}."
 * [Join team] [Decline]). `GET /api/team/invites/mine` lazily ATTACHES any
 * open invites addressed to the caller's auth-verified email first (CG-7's
 * claim-then-list pattern), so a fresh invite shows on the first page load
 * after sign-in. Accept → active membership (the ONLY path to active from an
 * email invite) · Decline → tombstone + 30-day re-invite cooldown. Flag
 * `coach_provisioning_v1` gates server-side (off → enabled:false, empty).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';

export interface MyTeamInvite {
  id: string;
  teamId: string;
  teamName: string;
  coachName: string;
}

export interface UseMyTeamInvitesResult {
  invites: MyTeamInvite[];
  /** Accept the invite → active roster membership. Resolves on success. */
  accept: (inviteId: string) => Promise<void>;
  /** Decline the invite (starts the server-side re-invite cooldown). */
  decline: (inviteId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

interface MineResponse {
  enabled?: boolean;
  invites?: MyTeamInvite[];
}

/** B202 — accept/decline reject with this, carrying the server body's
 *  `code` (e.g. `guardian_consent_required`, the `coppaHoldResponse` shape
 *  in api/index.js) so the banner can render the right line instead of a
 *  swallowed error. */
export interface InviteActionError extends Error {
  code?: string;
}

async function readActionErrorBody(res: Response): Promise<{ error?: string; code?: string }> {
  try {
    return (await res.json()) as { error?: string; code?: string };
  } catch {
    return {};
  }
}

export function useMyTeamInvites(): UseMyTeamInvitesResult {
  const { supabase, user } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const [invites, setInvites] = useState<MyTeamInvite[]>([]);
  const inflightRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !user) {
      setInvites([]);
      return;
    }
    const token = user.id;
    inflightRef.current = token;
    try {
      const res = await fetchAuthed('/api/team/invites/mine');
      if (!res.ok) return; // banner is best-effort — never surface an error UI
      const json = (await res.json()) as MineResponse;
      if (inflightRef.current !== token) return; // stale
      setInvites(json.enabled && Array.isArray(json.invites) ? json.invites : []);
    } catch {
      /* banner is best-effort */
    }
  }, [supabase, user, fetchAuthed]);

  useEffect(() => {
    void load();
  }, [load]);

  const accept = useCallback(
    async (inviteId: string) => {
      const res = await fetchAuthed(
        `/api/team/invites/${encodeURIComponent(inviteId)}/accept`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const body = await readActionErrorBody(res);
        const err = new Error(body.error || `accept failed (${res.status})`) as InviteActionError;
        err.code = body.code;
        throw err;
      }
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    },
    [fetchAuthed],
  );

  const decline = useCallback(
    async (inviteId: string) => {
      const res = await fetchAuthed(
        `/api/team/invites/${encodeURIComponent(inviteId)}/decline`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const body = await readActionErrorBody(res);
        const err = new Error(body.error || `decline failed (${res.status})`) as InviteActionError;
        err.code = body.code;
        throw err;
      }
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    },
    [fetchAuthed],
  );

  return { invites, accept, decline, refetch: load };
}
